/**
 * AIProgressBar — wyswietla postep operacji AI w real-time.
 *
 * Nasluchuje WebSocket event "ai_progress" z backendu.
 * Pokazuje: progress bar + label (generic, bez szczegolow pipelineu).
 * Znika po 2 min bez nowej ramki (WS nie wysyla zdarzenia konca zadania).
 *
 * Usage:
 *   <AIProgressBar />
 *   Umieszczony w AppShell — widoczny globalnie gdy AI pracuje.
 */

import { useState, useEffect, useRef } from "react";
import { Progress } from "@/components/ui/progress";
import { Loader2, Brain, CheckCircle2, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cancelAITask } from "@/lib/api";

export default function AIProgressBar() {
  const [progress, setProgress] = useState(null); // {stage, total, label, pct, module, sub_status}
  const [visible, setVisible] = useState(false);
  const hideTimer = useRef(null);

  useEffect(() => {
    // Listen for WebSocket messages
    const handleWsMessage = (event) => {
      try {
        const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        // Ramka WS z backendu to {"event": ..., "data": {...}} (ws_manager.py); `type` zostaje dla zdarzenia "ai_progress".
        const rodzaj = data.event ?? data.type;
        if (rodzaj === "ai_progress") {
          const payload = data.data || data;
          setProgress(payload);
          setVisible(true);

          // Clear any hide timer
          if (hideTimer.current) clearTimeout(hideTimer.current);

          // UI-FIX: stage === total oznacza START ostatniego etapu, nie koniec pracy (backend nie wysyla zdarzenia konca),
          // wiec pasek nie twierdzi "Gotowe!" i nie znika po 3 s; chowa sie dopiero po 2 min bez nowej ramki.
          hideTimer.current = setTimeout(() => {
            setVisible(false);
            setProgress(null);
          }, 120000);
        } else if (rodzaj === "ai_substatus") {
          // 2026-04-27: granular sub_status update bez zmiany stage
          const payload = data.data || data;
          setProgress((prev) => prev ? { ...prev, sub_status: payload.sub_status } : prev);
        }
      } catch {
        // Not JSON or not our message
      }
    };

    // Also listen for custom event (fallback if WS not direct)
    const handleCustomEvent = (e) => {
      handleWsMessage({ data: JSON.stringify(e.detail) });
    };
    window.addEventListener("ai_progress", handleCustomEvent);

    // UI-FIX: AppShell rozsyla kazda ramke WS jako window "leslex:ws" {event, data} (instancja WS powstaje po efektach dzieci).
    const handleLeslexWs = (e) => {
      const ev = e.detail?.event;
      if (ev === "ai_progress" || ev === "ai_substatus") handleWsMessage({ data: { event: ev, data: e.detail.data } });
      else if (ev === "document_ready") {
        // jedyny zmierzony sygnal konca pracy nad dokumentem — wtedy pasek znika od razu
        if (hideTimer.current) clearTimeout(hideTimer.current);
        setVisible(false);
        setProgress(null);
      }
    };
    window.addEventListener("leslex:ws", handleLeslexWs);

    return () => {
      window.removeEventListener("ai_progress", handleCustomEvent);
      window.removeEventListener("leslex:ws", handleLeslexWs);
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, []);

  const isComplete = false; // brak zdarzenia konca zadania w WS — nie twierdzimy "Gotowe!"
  // backend zglasza stage = etap, ktory wlasnie SIE ZACZYNA (pct = stage/total), wiec pasek pokazuje etapy ukonczone.
  const ukonczoneEtapy = progress && progress.total > 0
    ? Math.max(0, Math.min(100, Math.round(((progress.stage - 1) / progress.total) * 100)))
    : 0;
  // 2026-04-27 (Faza 1.4): emoji per-module dla szybkiej identyfikacji wizualnej
  const moduleLabels = {
    advice_deep: "🧠 Porada poglebiona",
    generate: "✍️ Generowanie pisma",
    review: "🔍 Recenzja",
    advice_quick: "⚡ Porada szybka",
    tax_optimization: "💰 Optymalizacja podatkowa",
    contract_analyze: "📜 Analiza umowy",
    iterate: "♻️ Iteracja pisma",
    auto_fix: "🔧 Auto-poprawa pisma",
    advice_prep: "📂 Przygotowanie porady — czytanie plikow",
    contract_batch_prep: "📂 Przygotowanie analizy — czytanie plikow",
    studio_chat: "💬 Chat Studio",
    legal_search: "⚖️ Wyszukiwanie prawne",
    legal_deep: "🔬 Deep research prawny",
    due_diligence: "🏢 Due diligence",
  };

  return (
    <AnimatePresence>
      {visible && progress && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
          className="fixed top-4 left-1/2 -translate-x-1/2 z-[60] w-[400px] max-w-[90vw]"
        >
          <div className="bg-card border border-border rounded-xl shadow-2xl p-4">
            <div className="flex items-start gap-3 mb-2">
              {isComplete ? (
                <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
              ) : (
                <Loader2 className="h-5 w-5 text-primary animate-spin shrink-0 mt-0.5" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {isComplete ? "Gotowe!" : progress.label}
                </p>
                {/* 2026-04-27: detailed sub_status — co konkretnie AI robi
                    (np. "Pobieram art. 415 k.c.", "Analiza klauzuli 3 z 12") */}
                {progress.sub_status && !isComplete && (
                  <p className="text-xs text-primary/80 italic mt-0.5 truncate animate-pulse">
                    {progress.sub_status}
                  </p>
                )}
                <p className="text-xs text-muted-foreground mt-0.5">
                  {moduleLabels[progress.module] || "AI"} — Etap {progress.stage}/{progress.total}
                </p>
              </div>
              {/* 2026-04-27: Cancel button (UX 2026) — pojawia sie tylko gdy task_id w payloadzie */}
              {!isComplete && progress.task_id ? (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await cancelAITask(progress.task_id);
                      setVisible(false);
                      setProgress(null);
                    } catch (err) {
                      // Silent fail — task moze juz byc skonczony
                      console.warn("Cancel failed:", err);
                    }
                  }}
                  className="text-muted-foreground/60 hover:text-destructive shrink-0 mt-0.5 transition-colors"
                  title="Anuluj operacje"
                  aria-label="Anuluj operacje AI"
                >
                  <X className="h-4 w-4" />
                </button>
              ) : (
                <Brain className="h-4 w-4 text-muted-foreground/50 shrink-0 mt-0.5" />
              )}
            </div>
            <Progress
              value={ukonczoneEtapy}
              className="h-2"
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
